import {Button, Card, Col, Divider, Form, Input, message, Modal, Row, Space, Table, Tag} from "antd";
import {EyeOutlined, SearchOutlined} from "@ant-design/icons";
import React, {useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {IDENTIFICATION_SOURCE, NOT_AVAILABLE, YYYY_MM_DD_HH_MM} from "../../../util/Constants";
import {ComplaintRequestDto} from "../../../dto/ComplaintRequestDto";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import {InputRef} from "antd/es/input";
import customerChangeRequestAdminService from "../../../service/customerChangeRequestAdminService";
import {MSG_COMPLAINTS_LIST_FETCH_FAILURE_ERROR} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";

const {TextArea} = Input;
type DataIndex = keyof ComplaintRequestDto;

const ComplaintsUpdateRequestListComp = () => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [complaintsList, setComplaintsList] = useState<ComplaintRequestDto[]>()
    const [form] = Form.useForm();
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns = [
        {
            title: 'Complaint ID',
            dataIndex: 'complaintId',
            key: 'complaintId',
        },
        {
            title: 'CIN',
            dataIndex: 'cin',
            key: 'cin',
            sorter: (a: any, b: any) => {
                a = a.cin == null ? '' : a.cin
                b = b.cin == null ? '' : b.cin
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('cin', 'CIN'),
        },
        {
            title: 'Customer Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: any, b: any) => {
                a = a.name == null ? '' : a.name
                b = b.name == null ? '' : b.name
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('name', 'Customer Name'),
        },
        {
            title: 'Identification Type',
            dataIndex: 'identificationType',
            key: 'identificationType',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.identificationType == null ? '' : a.identificationType
                b = b.identificationType == null ? '' : b.identificationType
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationType', 'Identification Type'),
            render: (text: any, rec: any) => {
                return ( <Tag
                        className={getClassName(text)}
                        color={getColor(text)} key={text}>
                        {null != text ? text.toUpperCase() : 'N/A'}
                    </Tag>
                )
            }
        },
        {
            title: 'Identification Value',
            dataIndex: 'identificationValue',
            key: 'identificationValue',
            sorter: (a: any, b: any) => {
                a = a.identificationValue == null ? '' : a.identificationValue
                b = b.identificationValue == null ? '' : b.identificationValue
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationValue', 'Identification Value'),
        },
        {
            title: 'Request Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            sorter: (a: any, b: any) => {
                a = a.createdDate == null ? '' : a.createdDate
                b = b.createdDate == null ? '' : b.createdDate
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (rec: ComplaintRequestDto) => (
                <Space size="middle">
                    <Button className='btn-action-outline-common btn-action-view '
                            onClick={() => showModal(rec)}
                            icon={<EyeOutlined/>}>View</Button>
                </Space>
            ),
        },
    ];

    const getClassName = (text: string) => {
        if (null != text){
            return text.toUpperCase() == "DRIVING LICENSE" ? 'drl-tbl-tag' : 'tbl-tag'
        }
        return  'tbl-tag';
    }
    const showModal = (record: ComplaintRequestDto) => {
        setIsModalOpen(true);
        form.setFieldsValue({complaint: record.complaint})
    };
    const getColor = (identificationSourceType: string) => {
        switch (identificationSourceType) {
            case IDENTIFICATION_SOURCE.NIC: {
                return 'green';
            }
            case IDENTIFICATION_SOURCE.PP: {
                return 'geekblue';
            }
            case IDENTIFICATION_SOURCE.DL: {
                return 'volcano';
            }
            default:{
                return '#8c8c8c';
            }
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false);
    };
    useEffect(() => {
        fetchComplaintsList()
    }, []);

    const fetchComplaintsList = () => {
        setLoading(true)
        customerChangeRequestAdminService.getPendingComplaints().then((res) => {
            if (res.data.results) {
                setComplaintsList(res.data.results)
                setLoading(false)
            } else {
                message.error(MSG_COMPLAINTS_LIST_FETCH_FAILURE_ERROR)
                setLoading(false)
            }
        }).catch(reason => {
            ErrorHandler.handleError(reason)
            setLoading(false)
        })
    }
    return (
        <>
            <Row gutter={[24, 12]}>
                <Col span={24}>

                </Col>
                <Col span={24}>
                    <Table size='small' dataSource={complaintsList} columns={columns} scroll={{x: 'max-content'}}
                           loading={isLoading}></Table>
                </Col>
            </Row>
            <Modal title="View Complaint" open={isModalOpen} footer={null} onCancel={handleCancel} width={800} centered
                   className={'modal-body-scroll-only disabled-data-show-inputs-container'} style={{top: 20, paddingBottom: 20}}>
                <Row gutter={[24, 12]} className={'form-item-mb-12 modal-body-scroll-only-main-row'} style={{marginTop: 18}}>
                    <Col span={24}>
                        <Card title="Complaint Details" size={'small'}>
                            <Form
                                className='form-input'
                                layout={"vertical"}
                                form={form}
                            >
                                <Row gutter={[12, 0]}>
                                    <Col span={24}>
                                        <Form.Item
                                            name={"complaint"}>
                                            <TextArea rows={4} disabled/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Card>
                    </Col>
                    <Col span={24}>
                        <Divider style={{marginBottom: 12}}></Divider>
                    </Col>
                    <Col span={24}>
                        <Space style={{float: 'right'}}>
                            <Button
                                    onClick={handleCancel}
                                    style={{
                                        width: 80,
                                    }}>Cancel</Button>
                        </Space>
                    </Col>
                </Row>

            </Modal>
        </>
    )
}
export default ComplaintsUpdateRequestListComp;